import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import { AuthContext } from '../../../context/AuthContext';
import { Username } from '../../../utils/privacyMask';
import '../ServerData.css';
import './ChecklistPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

function getToken() { return localStorage.getItem('token'); }
function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const PRIORITY_LABEL = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
const PRIORITY_RANK  = { urgent: 4, high: 3, medium: 2, low: 1 };
const PRIORITY_ICON  = { urgent: 'bi-fire', high: 'bi-arrow-up-circle-fill', medium: 'bi-dash-circle-fill', low: 'bi-arrow-down-circle-fill' };

const STATUS_FILTERS = ['all', 'open', 'done', 'removed'];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function toDateTimeLocal(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function isOverdue(item) {
  return item.status === 'open' && item.dueAt && new Date(item.dueAt).getTime() <= Date.now();
}

function PriorityBadge({ priority }) {
  if (!priority) return null;
  return (
    <span className={`sd-badge sd-badge-priority-${priority}`}>
      <i className={`bi ${PRIORITY_ICON[priority]}`} /> {PRIORITY_LABEL[priority]}
    </span>
  );
}

function ChecklistItemCard({ item, onUpdate }) {
  const [open, setOpen]   = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [text, setText]       = useState(item.text || '');
  const [assignee, setAssignee] = useState(item.assigneeUsername || '');
  const [dueAt, setDueAt]       = useState(toDateTimeLocal(item.dueAt));
  const [priority, setPriority] = useState(item.priority || '');
  const { user } = useContext(AuthContext);

  const overdue = isOverdue(item);

  const patch = async (body) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/botdata/checklist/${item.seqId ?? item._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const markDone = () => patch({
    status: 'done',
    doneById: 'dashboard',
    doneByUsername: user?.username || 'Dashboard',
    doneAt: new Date().toISOString(),
  });

  const reopen = () => patch({
    status: 'open',
    doneById: null, doneByUsername: null, doneAt: null,
    removedById: null, removedByUsername: null, removedAt: null,
  });

  const remove = () => patch({
    status: 'removed',
    removedById: 'dashboard',
    removedByUsername: user?.username || 'Dashboard',
    removedAt: new Date().toISOString(),
  });

  const saveEdits = async () => {
    await patch({
      text,
      assigneeUsername: assignee || null,
      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      priority: priority || null,
    });
    setEditing(false);
  };

  return (
    <div className={`sd-card${overdue ? ' cl-card-overdue' : ''}`}>
      <div className="sd-card-header" onClick={() => setOpen(o => !o)}>
        <div className="sd-card-left">
          <div className="sd-card-meta">
            {item.botId && <span className="sd-bot-pill"><i className="bi bi-robot" />{item.botId}</span>}
            {item.createdByUsername && <span><i className="bi bi-person-check" /> <Username value={item.createdByUsername} /></span>}
            {item.guildName && <span><i className="bi bi-building" /> {item.guildName}</span>}
            {item.assigneeUsername && (
              <span className="cl-assignee-pill"><i className="bi bi-person-fill" /> <Username value={item.assigneeUsername} /></span>
            )}
            {item.dueAt && (
              <span style={{ color: overdue ? '#f87171' : 'var(--color-muted)' }}>
                <i className="bi bi-alarm" /> Due: {formatDate(item.dueAt)}
              </span>
            )}
            {overdue && <span className="sd-badge sd-badge-urgent"><i className="bi bi-exclamation-triangle-fill" /> Overdue</span>}
            <PriorityBadge priority={item.priority} />
          </div>
          <div className="sd-card-content">{item.text}</div>
        </div>
        <div className="sd-card-actions">
          <span className={`sd-badge sd-badge-${item.status === 'done' ? 'resolved' : item.status === 'removed' ? 'rejected' : 'open'}`}>{item.status}</span>
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: '0.8rem' }} />
        </div>
      </div>

      {open && (
        <div className="sd-detail">
          <div className="sd-field-group">
            <div className="sd-field-label">Created</div>
            <div className="sd-field-value">{formatDate(item.createdAt)} by {item.createdByUsername ? <Username value={item.createdByUsername} /> : '—'}</div>
          </div>
          {item.doneAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Done</div>
              <div className="sd-field-value">{formatDate(item.doneAt)} by {item.doneByUsername ? <Username value={item.doneByUsername} /> : '—'}</div>
            </div>
          )}
          {item.removedAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Removed</div>
              <div className="sd-field-value">{formatDate(item.removedAt)} by {item.removedByUsername ? <Username value={item.removedByUsername} /> : '—'}</div>
            </div>
          )}

          {editing ? (
            <>
              <div className="sd-field-group">
                <div className="sd-field-label">Text</div>
                <textarea className="sd-textarea" value={text} onChange={e => setText(e.target.value)} />
              </div>
              <div className="cl-edit-row">
                <div className="sd-field-group">
                  <div className="sd-field-label">Assignee</div>
                  <input className="cl-input" placeholder="Discord/Telegram username" value={assignee} onChange={e => setAssignee(e.target.value)} />
                </div>
                <div className="sd-field-group">
                  <div className="sd-field-label">Due</div>
                  <input className="cl-input" type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} />
                </div>
                <div className="sd-field-group">
                  <div className="sd-field-label">Priority</div>
                  <select className="cl-input" value={priority} onChange={e => setPriority(e.target.value)}>
                    <option value="">None</option>
                    {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                  </select>
                </div>
              </div>
              <div className="sd-action-row">
                <button className="sd-btn sd-btn-resolve" onClick={saveEdits} disabled={saving}>
                  <i className="bi bi-check-circle" /> Save
                </button>
                <button className="sd-btn sd-btn-neutral" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="sd-action-row">
              {item.status === 'open' && (
                <button className="sd-btn sd-btn-approve" onClick={markDone} disabled={saving}>
                  <i className="bi bi-check-circle" /> Mark Done
                </button>
              )}
              {item.status !== 'open' && (
                <button className="sd-btn sd-btn-neutral" onClick={reopen} disabled={saving}>
                  <i className="bi bi-arrow-counterclockwise" /> Reopen
                </button>
              )}
              <button className="sd-btn sd-btn-neutral" onClick={() => setEditing(true)} disabled={saving}>
                <i className="bi bi-pencil" /> Edit
              </button>
              {item.status !== 'removed' && (
                <button className="sd-btn sd-btn-reject" onClick={remove} disabled={saving}>
                  <i className="bi bi-trash" /> Remove
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateChecklistModal({ onClose, onCreated }) {
  const { user } = useContext(AuthContext);
  const [text, setText]         = useState('');
  const [assignee, setAssignee] = useState('');
  const [dueAt, setDueAt]       = useState('');
  const [priority, setPriority] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const submit = async () => {
    if (!text.trim()) { setError('Text is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/botdata/checklist`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          text: text.trim(),
          assigneeUsername: assignee || null,
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          priority: priority || null,
          botId: 'dashboard',
          createdById: 'dashboard',
          createdByUsername: user?.username || 'Dashboard',
        }),
      });
      if (res.ok) {
        onCreated();
        onClose();
      } else {
        setError('Failed to create item');
      }
    } catch {
      setError('Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cl-modal-overlay" onClick={onClose}>
      <div className="cl-modal-panel" onClick={e => e.stopPropagation()}>
        <div className="cl-modal-header">
          <span><i className="bi bi-list-check me-2" />New Checklist Item</span>
          <button className="cl-modal-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>
        <div className="cl-modal-body">
          <div className="sd-field-group" style={{ marginBottom: 14 }}>
            <div className="sd-field-label">Task</div>
            <textarea
              className="sd-textarea"
              placeholder="What needs to be done?"
              value={text}
              onChange={e => setText(e.target.value)}
              autoFocus
            />
          </div>
          <div className="cl-edit-row">
            <div className="sd-field-group">
              <div className="sd-field-label">Assignee (optional)</div>
              <input className="cl-input" placeholder="Discord/Telegram username" value={assignee} onChange={e => setAssignee(e.target.value)} />
            </div>
            <div className="sd-field-group">
              <div className="sd-field-label">Due (optional)</div>
              <input className="cl-input" type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} />
            </div>
            <div className="sd-field-group">
              <div className="sd-field-label">Priority</div>
              <select className="cl-input" value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="">None</option>
                {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
            </div>
          </div>
          {error && <div style={{ color: '#f87171', fontSize: '0.8rem', marginTop: 10 }}>{error}</div>}
        </div>
        <div className="cl-modal-footer">
          <button className="sd-btn sd-btn-neutral" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="sd-btn sd-btn-approve" onClick={submit} disabled={saving}>
            <i className="bi bi-plus-circle" /> Create
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChecklistPage() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter]     = useState('open');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [overdueOnly, setOverdueOnly]       = useState(false);
  const [sortBy, setSortBy]                 = useState('priority');
  const [showCreate, setShowCreate]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/botdata/checklist${params}`, { headers: authHeaders() });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all:     items.length,
    open:    items.filter(i => i.status === 'open').length,
    done:    items.filter(i => i.status === 'done').length,
    removed: items.filter(i => i.status === 'removed').length,
  }), [items]);

  const overdueCount = useMemo(() => items.filter(isOverdue).length, [items]);

  const distinctAssignees = useMemo(
    () => [...new Set(items.map(i => i.assigneeUsername).filter(Boolean))].sort(),
    [items]
  );

  const visibleItems = useMemo(() => {
    const filtered = items
      .filter(i => !priorityFilter || (priorityFilter === 'none' ? !i.priority : i.priority === priorityFilter))
      .filter(i => !assigneeFilter || i.assigneeUsername === assigneeFilter)
      .filter(i => !overdueOnly || isOverdue(i));

    return [...filtered].sort((a, b) => {
      if (sortBy === 'priority') {
        const diff = (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0);
        if (diff !== 0) return diff;
      }
      if (sortBy === 'due') {
        if (!a.dueAt && !b.dueAt) return 0;
        if (!a.dueAt) return 1;
        if (!b.dueAt) return -1;
        return new Date(a.dueAt) - new Date(b.dueAt);
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [items, priorityFilter, assigneeFilter, overdueOnly, sortBy]);

  return (
    <div className="sd-page">
      <Sidebar title="Checklist" />

      <div className="sd-header">
        <div className="sd-title">
          <i className="bi bi-list-check" />
          Checklist
        </div>
        <div className="sd-filters">
          {STATUS_FILTERS.map(f => (
            <button
              key={f}
              className={`sd-filter-btn${statusFilter === f ? ' active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {counts[f] > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>({counts[f]})</span>}
            </button>
          ))}
          <button
            className={`sd-filter-btn${overdueOnly ? ' active' : ''}`}
            onClick={() => setOverdueOnly(o => !o)}
            style={overdueOnly ? { borderColor: 'rgba(239,68,68,0.5)', color: '#f87171', background: 'rgba(239,68,68,0.1)' } : undefined}
          >
            <i className="bi bi-exclamation-triangle-fill" /> Overdue{overdueCount > 0 && ` (${overdueCount})`}
          </button>
          <button className="sd-filter-btn" onClick={load} title="Refresh">
            <i className="bi bi-arrow-clockwise" />
          </button>
          <button className="sd-btn sd-btn-approve" onClick={() => setShowCreate(true)}>
            <i className="bi bi-plus-circle" /> New Item
          </button>
        </div>
      </div>

      <div className="sd-filters" style={{ marginBottom: 16 }}>
        <select className="sd-filter-btn" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          <option value="">All priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
          <option value="none">No priority</option>
        </select>
        <select className="sd-filter-btn" value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
          <option value="">All assignees</option>
          {distinctAssignees.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="sd-filter-btn" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="priority">Sort: Priority</option>
          <option value="due">Sort: Due Date</option>
          <option value="created">Sort: Created</option>
        </select>
      </div>

      <div className="sd-stats">
        {[
          { label: 'Total', value: items.length, color: '#e2e8f0' },
          { label: 'Open', value: counts.open, color: '#c4a96a' },
          { label: 'Overdue', value: overdueCount, color: '#f87171' },
          { label: 'Done', value: counts.done, color: '#4ade80' },
          { label: 'Urgent', value: items.filter(i => i.status === 'open' && i.priority === 'urgent').length, color: '#f87171' },
        ].map(s => (
          <div className="sd-stat-pill" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(4)].map((_, i) => <div key={i} className="sd-skel" style={{ height: 68, borderRadius: 14 }} />)}
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="sd-empty"><i className="bi bi-check2-all" /><p>No checklist items found</p></div>
      ) : (
        visibleItems.map(item => <ChecklistItemCard key={String(item._id)} item={item} onUpdate={load} />)
      )}

      {showCreate && <CreateChecklistModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}
